import React from "react";
import { GlobalVariableRegistry, DecisionVariablesRegistry } from "../utils/types";
import { 
  ModalState, 
  PlaceholderModalState, 
  DeleteModalState, 
  PreviewModalState, 
  StandaloneModalState 
} from "../hooks/hooks";
import NodeModal from "./modals/NodeModal";
import PlaceholderModal from "./modals/PlaceholderModal";
import DeleteModals from "./modals/DeleteModals";
import PreviewModal from "./modals/PreviewModal";

import ToolCallModal from "./modals/ToolCallModal";

interface ModalsProps {
  // Modal states
  modalState: ModalState;
  placeholderModalState: PlaceholderModalState;
  deleteModalState: DeleteModalState;
  previewModalState: PreviewModalState;
  standaloneModalState: StandaloneModalState;
  isResetModalVisible: boolean;
  isDeleteInterjectionGroupModalVisible: boolean;
  interjectionGroupToDelete: any | null;
  
  // Tool call modal external control
  externalToolCallModal?: {
    visible: boolean;
    toolCall: any;
    nodeId: string;
    isGlobalEdit: boolean;
    originalSignature: string;
  } | null;
  onExternalToolCallEdit?: (nodeId: string, toolCall: any) => void;
  onExternalToolCallCancel?: () => void;
  
  // Data
  isFirstNode: boolean;
  identifiers: string[];
  searchText: string;
  globalVariableRegistry: GlobalVariableRegistry;
  decisionVariablesRegistry: DecisionVariablesRegistry;
  edges: any[];
  nodes: any[];
  
  // State setters
  setModalState: (state: ModalState | ((prev: ModalState) => ModalState)) => void;
  setPlaceholderModalState: (state: PlaceholderModalState | ((prev: PlaceholderModalState) => PlaceholderModalState)) => void;
  setDeleteModalState: (state: DeleteModalState | ((prev: DeleteModalState) => DeleteModalState)) => void;
  setPreviewModalState: (state: PreviewModalState | ((prev: PreviewModalState) => PreviewModalState)) => void;
  setStandaloneModalState: (state: StandaloneModalState | ((prev: StandaloneModalState) => StandaloneModalState)) => void;
  setEdges: (edges: any[] | ((prev: any[]) => any[])) => void;
  setSearchText: (text: string) => void;
  
  // Event handlers
  onOk: () => void;
  onCancel: () => void;
  onPlaceholderModalCancel: () => void;
  onDeleteModalCancel: () => void;
  onDeleteNodeConfirm: () => void;
  onDeleteBranchConfirm: () => void;
  onResetConfirm: () => void;
  onResetCancel: () => void;
  onStandaloneOk: () => void;
  onStandaloneCancel: () => void;
  onPreviewModalClose: () => void;
  onPreviewPlaceholderClick: (nodeId: string, placeholderName: string) => void;
  onDeleteInterjectionGroupConfirm: () => void;
  onDeleteInterjectionGroupCancel: () => void;
  onCreateDecisionVariable?: (variableData: { name: string; type: string }) => void;
  onCreateNodeForDecisionPath?: (pathType: 'true' | 'false', sourceNodeId: string) => Promise<string>;
  
  // Utility functions
  addIdentifier: (value: string) => void;
  hasChildWithIdentifier: (parentId: string, identifier: string) => boolean;
  getDisabledIdentifiers: (parentId: string) => string[];
  getAvailableIdentifiers: (excludeNodeId?: string) => string[];
  isIdentifierInUse: (identifier: string, excludeNodeId?: string) => boolean;
  getCurrentExamples: () => string[];
  addExampleToPlaceholder: () => void;
  removeExampleFromPlaceholder: (index: number) => void;
}

const Modals: React.FC<ModalsProps> = ({
  modalState,
  placeholderModalState,
  deleteModalState,
  previewModalState,
  standaloneModalState,
  isResetModalVisible,
  isDeleteInterjectionGroupModalVisible,
  interjectionGroupToDelete,
  externalToolCallModal,
  onExternalToolCallEdit,
  onExternalToolCallCancel,

  isFirstNode,
  identifiers,
  searchText,
  globalVariableRegistry,
  decisionVariablesRegistry,
  edges,
  nodes,
  setModalState,
  setPlaceholderModalState,
  setDeleteModalState,
  setPreviewModalState,
  setStandaloneModalState,
  setEdges,
  setSearchText,
  onOk,
  onCancel,
  onPlaceholderModalCancel,
  onDeleteModalCancel,
  onDeleteNodeConfirm,
  onDeleteBranchConfirm,
  onResetConfirm,
  onResetCancel,
  onStandaloneOk,
  onStandaloneCancel,
  onPreviewModalClose,
  onPreviewPlaceholderClick,
  onDeleteInterjectionGroupConfirm,
  onDeleteInterjectionGroupCancel,
  onCreateDecisionVariable,
  onCreateNodeForDecisionPath,
  addIdentifier,
  hasChildWithIdentifier,
  getDisabledIdentifiers,
  getAvailableIdentifiers,
  isIdentifierInUse,
  getCurrentExamples,
  addExampleToPlaceholder,
  removeExampleFromPlaceholder,
}: ModalsProps) => {
  // Convert StandaloneModalState to ModalState for reusing NodeModal
  const convertStandaloneToModalState = (standaloneState: StandaloneModalState): ModalState => {
    return {
      visible: standaloneState.visible,
      modalTitle: "Create Standalone Node",
      newNodeContent: standaloneState.standaloneNodeContent,
      newNodePrefix: standaloneState.standaloneNodePrefix,
      newNodeSuffix: standaloneState.standaloneNodeSuffix,
      decisionData: standaloneState.standaloneDecisionData,
      sourceNodeId: null, // Key difference - no parent node for standalone
      newNodeEntityType: standaloneState.standaloneNodeEntityType,
      newNodeIdentifier: standaloneState.standaloneIdentifier,
      nextNodeId: standaloneState.standaloneNextNodeId,
      isDestinationNode: standaloneState.standaloneIsDestination,
      potentialStart: standaloneState.standalonePotentialStart,
      placeholders: {}, // Empty for standalone nodes
      isEditMode: false, // Always creating new nodes
      nodeToEdit: null,
      nodeToDelete: null,
      hasToolCall: standaloneState.standaloneHasToolCall,
      toolCalls: standaloneState.standaloneToolCalls,
      variableAssignments: standaloneState.standaloneVariableAssignments,
      // Preview modal properties (not used for standalone)
      isPreviewModalVisible: false,
      previewNodeId: null,
      conversationPath: [],
      previewSessionId: "",
      // Placeholder modal properties (not used for standalone)
      currentPlaceholder: "",
      newExample: "",
      editNodeId: null,
      isNewVariable: false,
    };
  };

  // Convert ModalState back to StandaloneModalState
  const convertModalToStandaloneState = (modalState: ModalState): StandaloneModalState => {
    return {
      visible: modalState.visible,
      standaloneNodeEntityType: modalState.newNodeEntityType,
      standaloneNodeContent: modalState.newNodeContent,
      standaloneNodePrefix: modalState.newNodePrefix,
      standaloneNodeSuffix: modalState.newNodeSuffix,
      standaloneDecisionData: modalState.decisionData,
      standaloneIdentifier: modalState.newNodeIdentifier,
      standaloneNextNodeId: modalState.nextNodeId,
      standaloneIsDestination: modalState.isDestinationNode,
      standalonePotentialStart: modalState.potentialStart,
      standaloneHasToolCall: modalState.hasToolCall,
      standaloneToolCalls: modalState.toolCalls,
      standaloneVariableAssignments: modalState.variableAssignments,
      nodePosition: standaloneModalState.nodePosition, // Preserve position
    };
  };

  return (
    <>
      <NodeModal
        modalState={modalState}
        isFirstNode={isFirstNode}
        identifiers={identifiers}
        searchText={searchText}
        globalVariableRegistry={globalVariableRegistry}
        decisionVariablesRegistry={decisionVariablesRegistry}
        nodes={nodes}
        edges={edges}
        setModalState={setModalState}
        setEdges={setEdges}
        setSearchText={setSearchText}
        onOk={onOk}
        onCancel={onCancel}
        addIdentifier={addIdentifier}
        hasChildWithIdentifier={hasChildWithIdentifier}
        getDisabledIdentifiers={getDisabledIdentifiers}
        getAvailableIdentifiers={getAvailableIdentifiers}
        isIdentifierInUse={isIdentifierInUse}
        onCreateDecisionVariable={onCreateDecisionVariable}
        onCreateNodeForDecisionPath={onCreateNodeForDecisionPath}
      />

      <PlaceholderModal
        placeholderModalState={placeholderModalState}
        globalVariableRegistry={globalVariableRegistry}
        setPlaceholderModalState={setPlaceholderModalState}
        onCancel={onPlaceholderModalCancel}
        getCurrentExamples={getCurrentExamples}
        addExampleToPlaceholder={addExampleToPlaceholder}
        removeExampleFromPlaceholder={removeExampleFromPlaceholder}
      />

      <DeleteModals
        deleteModalState={deleteModalState}
        isResetModalVisible={isResetModalVisible}
        isDeleteInterjectionGroupModalVisible={isDeleteInterjectionGroupModalVisible}
        interjectionGroupToDelete={interjectionGroupToDelete}
        edges={edges}
        setDeleteModalState={setDeleteModalState}
        onDeleteModalCancel={onDeleteModalCancel}
        onDeleteNodeConfirm={onDeleteNodeConfirm}
        onDeleteBranchConfirm={onDeleteBranchConfirm}
        onResetConfirm={onResetConfirm}
        onResetCancel={onResetCancel}
        onDeleteInterjectionGroupConfirm={onDeleteInterjectionGroupConfirm}
        onDeleteInterjectionGroupCancel={onDeleteInterjectionGroupCancel}
      />

      <PreviewModal
        previewModalState={previewModalState}
        globalVariableRegistry={globalVariableRegistry}
        nodes={nodes}
        setPreviewModalState={setPreviewModalState}
        onClose={onPreviewModalClose}
        onPlaceholderClick={onPreviewPlaceholderClick}
      />

      <NodeModal
        modalState={convertStandaloneToModalState(standaloneModalState)}
        isFirstNode={false} // Standalone nodes are never first nodes
        identifiers={identifiers}
        searchText={searchText}
        globalVariableRegistry={globalVariableRegistry}
        decisionVariablesRegistry={decisionVariablesRegistry}
        nodes={nodes}
        edges={edges}
        setModalState={(state) => {
          if (typeof state === 'function') {
            const currentModalState = convertStandaloneToModalState(standaloneModalState);
            const newModalState = state(currentModalState);
            setStandaloneModalState(convertModalToStandaloneState(newModalState));
          } else {
            setStandaloneModalState(convertModalToStandaloneState(state));
          }
        }}
        setEdges={setEdges}
        setSearchText={setSearchText}
        onOk={onStandaloneOk}
        onCancel={onStandaloneCancel}
        addIdentifier={addIdentifier}
        hasChildWithIdentifier={() => false} // Standalone nodes have no children initially
        getDisabledIdentifiers={() => []} // No disabled identifiers for standalone nodes
        getAvailableIdentifiers={getAvailableIdentifiers}
        isIdentifierInUse={isIdentifierInUse}
        onCreateDecisionVariable={onCreateDecisionVariable}
        onCreateNodeForDecisionPath={onCreateNodeForDecisionPath}
      />

      {/* External Tool Call Modal for both global and single node edits */}
      {externalToolCallModal && (
        <ToolCallModal
          visible={externalToolCallModal.visible}
          toolCall={externalToolCallModal.toolCall}
          nodeId={externalToolCallModal.nodeId}
          isGlobalEdit={externalToolCallModal.isGlobalEdit}
          originalSignature={externalToolCallModal.originalSignature}
          globalVariableRegistry={globalVariableRegistry}
          onOk={onExternalToolCallEdit || (() => {})}
          onCancel={onExternalToolCallCancel || (() => {})}
        />
      )}
    </>
  );
};

export default Modals; 